import { TypedDocumentNode, gql } from '@apollo/client';
import { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { sortByDate } from '@sorare/core/src/lib/dates';

import { isGameLive, isGameScheduled } from 'lib/so5';

import { So5AppearenceInlineGamesDots_so5Appearance } from './__generated__/index.graphql';

const Wrapper = styled(Horizontal)`
  --size-unit: 1;

  gap: 1px;
  color: var(--c-neutral-500);
  font: var(--t-label-xs);
  max-width: 100%;
`;
const PropablePitcher = styled.span`
  color: var(--c-green-600);
  border: 1px solid var(--c-green-300);
  border-radius: 50%;
  padding: calc(var(--size-unit) * 2px);
  font-size: calc(var(--size-unit) * 8px);
`;

const EmptyCircle = styled.span`
  font-size: calc(var(--size-unit) * 10px);
  color: var(--c-neutral-800);
`;

const FullCircle = styled.span`
  font-size: calc(var(--size-unit) * 9px);
  color: rgba(var(--c-rgb-green-600), 0.45);
`;

const Cross = styled.span`
  font-size: calc(var(--size-unit) * 10px);
  color: rgba(var(--c-rgb-red-300), 0.85);
`;

type Props = {
  so5Appearance: So5AppearenceInlineGamesDots_so5Appearance;
  sizeUnit?: 'sm' | 'md' | 'lg' | 'xl';
  isStartingPitcher?: boolean;
  pitchingGames?: So5AppearenceInlineGamesDots_so5Appearance['eligiblePlayerGameScores'];
};

const sizeUnitsMap = {
  sm: 0.9,
  md: 1.1,
  lg: 1.5,
  xl: 1.8,
};

export const So5AppearenceInlineGamesDots = ({
  so5Appearance,
  sizeUnit = 'md',
  isStartingPitcher,
  pitchingGames = [],
}: Props) => {
  const { eligiblePlayerGameScores, playedGames } = so5Appearance;

  const sortedGames = sortByDate(
    [...eligiblePlayerGameScores],
    playerGameScore => playerGameScore.anyGame.date
  );

  return (
    <Wrapper style={{ '--size-unit': sizeUnitsMap[sizeUnit] } as CSSProperties}>
      {sortedGames.map(pgs => {
        const DNP = <Cross>✕</Cross>;
        const hasPlayed = playedGames.some(g => g.id === pgs.anyGame.id);
        let status: ReactNode = hasPlayed ? <FullCircle>●</FullCircle> : DNP;

        if (isGameLive(pgs.anyGame.statusTyped)) {
          status = <LiveDot animate size="md" color="var(--c-green-600)" />;
        }
        if (isGameScheduled(pgs.anyGame.statusTyped)) {
          status = <EmptyCircle>○</EmptyCircle>;
        }

        if (isStartingPitcher) {
          const isPlayerPitchingThisGame =
            pitchingGames[0]?.anyGame.id === pgs.anyGame.id;

          status = isPlayerPitchingThisGame ? <FullCircle>●</FullCircle> : DNP;

          if (isGameLive(pgs.anyGame.statusTyped)) {
            status = isPlayerPitchingThisGame ? (
              <LiveDot animate size="md" color="var(--c-green-600)" />
            ) : (
              DNP
            );
          }
          if (isGameScheduled(pgs.anyGame.statusTyped)) {
            status = isPlayerPitchingThisGame ? (
              <PropablePitcher>PP</PropablePitcher>
            ) : (
              DNP
            );
          }
        }

        return <span key={pgs.anyGame.id}>{status}</span>;
      })}
    </Wrapper>
  );
};

So5AppearenceInlineGamesDots.fragments = {
  so5Appearance: gql`
    fragment So5AppearenceInlineGamesDots_so5Appearance on So5AppearanceInterface {
      id
      scoreStatus
      eligiblePlayerGameScores {
        id
        anyGame {
          id
          statusTyped
          date
        }
      }
      playedGames {
        id
        date
      }
    }
  ` as TypedDocumentNode<So5AppearenceInlineGamesDots_so5Appearance>,
};
